/**
 * YT Power Surcharge — v2.0.0
 * Production-ready surcharge addon for Yateem Optics
 *
 * Fixes over v1.2.0:
 *  - Initial add with qty>1 now uses event.cartData.quantity (actual qty from Shopify response)
 *  - Same variant added twice with different properties tracked via PROP_PARENT_KEY (line key)
 *  - Race conditions: _pendingSync flag queues syncs that occur while one is in progress
 *  - Cart total badge always updates (full refresh on every surcharge change, no DOM-patch)
 *  - Checkout gate uses /cart.js API verification; waits for in-progress sync before proceeding
 *
 * Surcharge variants:
 *   Tier 20 → 47526438764797  (AED 20)
 *   Tier 25 → 47526445285629  (AED 25)
 *   Tier 30 → 47526445318397  (AED 30)
 */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────────────
  var SURCHARGE_VARIANT_MAP = {
    '20': 47526438764797,
    '25': 47526445285629,
    '30': 47526445318397,
  };

  var PROP_IS_SURCHARGE   = '_yt_power_surcharge';
  var PROP_PARENT_VARIANT = '_yt_parent_variant_id';
  var PROP_PARENT_KEY     = '_yt_parent_key';   // Shopify cart line key — unique even for same variant
  var NOTICE_TTL_MS       = 8000;
  var SYNC_DEBOUNCE_MS    = 300;

  // ── State ────────────────────────────────────────────────────────────────
  var _pendingAdd  = null;
  var _isSyncing   = false;
  var _pendingSync = false;  // Set when DOM changes while a sync is running
  var _syncTimer   = null;

  // ── Helpers ──────────────────────────────────────────────────────────────
  function isScItem(item) {
    return !!(item.properties && item.properties[PROP_IS_SURCHARGE] === 'true');
  }

  function cartFetch(url, body) {
    return fetch(url, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  function getSurchargeVariantId(powerPrice) {
    return SURCHARGE_VARIANT_MAP[String(parseInt(powerPrice, 10))] || null;
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isCartPage() {
    return window.location.pathname === '/cart';
  }

  // Find the parent non-surcharge item for a given surcharge cart item.
  // Prefers exact line-key match (handles same variant / different properties);
  // falls back to variant_id for surcharges added before PROP_PARENT_KEY existed.
  function findParent(cartItems, sc) {
    var props     = sc.properties || {};
    var parentKey = props[PROP_PARENT_KEY];
    var parentVid = props[PROP_PARENT_VARIANT];
    var i;

    if (parentKey) {
      // Key is authoritative — if set but not found, parent was removed. Never fall
      // back to variant_id here, or a surcharge for a removed line would wrongly
      // latch onto a surviving line with the same variant (e.g. same lens, different qty).
      for (i = 0; i < cartItems.length; i++) {
        if (cartItems[i].key === parentKey && !isScItem(cartItems[i])) return cartItems[i];
      }
      return null; // Key present but parent gone → treat as orphan
    }

    // No key stored (legacy surcharge added before v2.0.0) — fall back to variant_id
    if (parentVid) {
      for (i = 0; i < cartItems.length; i++) {
        if (String(cartItems[i].variant_id) === String(parentVid) && !isScItem(cartItems[i])) {
          return cartItems[i];
        }
      }
    }
    return null;
  }

  // ── Cart refresh helpers ──────────────────────────────────────────────────
  // These do NOT manage _isSyncing — syncSurcharges() owns that state.

  function refreshCartDrawer() {
    var cartDrawer = getCartDrawer();
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return Promise.resolve();

    var sections = cartDrawer.getSectionsToRender().map(function (s) { return s.section; });

    return cartFetch('/cart/update.js', {
      sections    : sections,
      sections_url: window.location.pathname,
    }).then(function (data) {
      if (data && data.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }
    }).catch(function (err) {
      console.error('[YT Surcharge] refreshCartDrawer failed:', err);
    });
  }

  function refreshMainCart() {
    var mainCartEl = document.getElementById('main-cart');
    if (!mainCartEl) return Promise.resolve();

    var sectionId = mainCartEl.dataset.id;
    if (!sectionId) return Promise.resolve();

    return cartFetch('/cart/update.js', {
      sections    : [sectionId],
      sections_url: window.location.pathname,
    }).then(function (data) {
      if (!data || !data.sections || !data.sections[sectionId]) return;
      var html    = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
      var newBody = html.querySelector('.main-cart-render');
      var target  = document.querySelector('.main-cart-render');
      if (newBody && target) target.innerHTML = newBody.innerHTML;
    }).catch(function (err) {
      console.error('[YT Surcharge] refreshMainCart failed:', err);
    });
  }

  // ── Toast notice ──────────────────────────────────────────────────────────

  function injectNoticeStyles() {
    if (document.getElementById('yt-surcharge-styles')) return;
    var s = document.createElement('style');
    s.id = 'yt-surcharge-styles';
    s.textContent =
      '.yt-surcharge-notice{display:flex;align-items:flex-start;gap:14px;width:94%;padding:12px 14px;margin:15px auto 12px;' +
      'background:#e7e7f1;border:1px solid #182983;border-radius:12px;font-size:14px;line-height:1.6;color:#182983;' +
      'animation:ytNoticeIn .3s ease}' +
      '@keyframes ytNoticeIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}' +
      '.yt-surcharge-notice-icon{width: 32px;height: 32px;min-width: 32px;border: 2px solid #182983;border-radius: 50%;display: flex;align-items: center;justify-content: center;font-size: 16px;font-weight: 700;color: #182983;font-style: italic;}' +
      '.yt-surcharge-notice-text{flex:1}' +
      '.yt-surcharge-notice-close{background:#182983;text-align:center;border:none;cursor:pointer;font-size:20px;line-height:1;height:20px;width:20px;border-radius: 100%;' +
      'color:#fff;padding:0;opacity:.6;flex-shrink:0}' +
      '.yt-surcharge-notice-close:hover{opacity:1}' +
      '.yt-surcharge-cart-badge{font-size:12px;color:#b45309;background:#fef9c3;border:1px solid #fde68a;' +
      'border-radius:4px;padding:3px 7px;margin-top:4px;display:inline-block;line-height:1.4}' +
      '.yt-surcharge-qty-display{display:flex;align-items:center;gap:6px;font-size:13px;color:#666;padding:4px 0}' +
      '.yt-surcharge-lock-icon{font-size:14px}';
    document.head.appendChild(s);
  }

  function showSurchargeNotice(powerPrice) {
    injectNoticeStyles();
    var cartDrawer = getCartDrawer();
    if (!cartDrawer) return;

    var cartBody = cartDrawer.querySelector('[data-cart-drawer-body]');
    if (!cartBody) return;

    var existing = cartBody.querySelector('.yt-surcharge-notice');
    if (existing) existing.remove();

    var notice = document.createElement('div');
    notice.className = 'yt-surcharge-notice';
    notice.innerHTML =
      '<span class="yt-surcharge-notice-icon">&#8505;</span>' +
      '<span class="yt-surcharge-notice-text">This charge is linked to your Bella color lens order (<strong>AED ' + powerPrice + ' per lens</strong>). It cannot be removed separately.</span>' +
      '<button class="yt-surcharge-notice-close" aria-label="Dismiss">&times;</button>';

    notice.querySelector('.yt-surcharge-notice-close').addEventListener('click', function () {
      notice.remove();
    });

    cartBody.insertBefore(notice, cartBody.firstChild);

    setTimeout(function () {
      // if (notice.parentNode) notice.remove();
    }, NOTICE_TTL_MS);
  }


  // ── Step 1: Capture surcharge intent when ATC button clicked ─────────────
  //
  // We intentionally do NOT capture qty here. The actual confirmed qty comes
  // from Shopify's cart response in Step 2 (event.cartData.quantity), which is
  // always accurate even when form inputs are hidden or dynamically replaced.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.yt-addtocart-cta');
    if (!btn) return;

    var productForm = btn.closest('product-form');
    if (!productForm) return;

    var powerPrice = productForm.dataset.ytPowerPrice;
    if (!powerPrice) { _pendingAdd = null; return; }

    var surchargeVariantId = getSurchargeVariantId(powerPrice);
    if (!surchargeVariantId) { _pendingAdd = null; return; }

    var sphereLeft = productForm.querySelector('select[name="properties[Left Eye Sphere]"]');
    if (!sphereLeft || !sphereLeft.value || sphereLeft.value === '0.00') { _pendingAdd = null; return; }

    var variantInput = productForm.querySelector('input[name="id"]');

    _pendingAdd = {
      surchargeVariantId: surchargeVariantId,
      parentVariantId   : variantInput ? variantInput.value : null,
      powerPrice        : parseInt(powerPrice, 10),
    };
  }, true /* capture */);


  // ── Step 2: After main product added to cart → add surcharge ─────────────
  //
  // event.cartData is Shopify's /cart/add.js response for the parent item.
  // .quantity = actual qty of that line in cart (correct even for qty>1 adds).
  // .key      = unique cart line key (used for accurate parent tracking).
  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    if (event.source !== 'product-form') return;
    if (!_pendingAdd) return;

    var pending = _pendingAdd;
    _pendingAdd = null;

    var cartData  = event.cartData || {};
    var actualQty = cartData.quantity || 1;   // Actual qty from Shopify's confirmed response
    var parentKey = cartData.key    || null;  // Unique line key for accurate matching

    var cartDrawer = getCartDrawer();
    if (!cartDrawer) return;

    var sections = typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map(function (s) { return s.section; })
      : [];

    var properties = {};
    properties[PROP_IS_SURCHARGE]   = 'true';
    properties[PROP_PARENT_VARIANT] = pending.parentVariantId;
    if (parentKey) properties[PROP_PARENT_KEY] = parentKey;

    var body = {
      items: [{
        id        : pending.surchargeVariantId,
        quantity  : actualQty,
        properties: properties,
      }],
    };

    if (sections.length) {
      body.sections     = sections;
      body.sections_url = window.location.pathname;
    }

    _isSyncing = true; // Suppress observer callbacks during add + render

    fetch('/cart/add.js', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify(body),
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (data.status) {
        console.error('[YT Surcharge] Failed to add surcharge:', data);
        _isSyncing = false;
        return;
      }
      if (data.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }
      setTimeout(function () {
        _isSyncing = false;
        showSurchargeNotice(pending.powerPrice);
        // Run any sync that was queued while we were adding/rendering
        if (_pendingSync) { _pendingSync = false; syncSurcharges(); }
      }, 200);
    })
    .catch(function (err) {
      console.error('[YT Surcharge] Error adding surcharge:', err);
      _isSyncing = false;
    });
  });


  // ── Step 3: Sync surcharges — API-based with line-key matching ────────────
  //
  // Fetches the authoritative cart state from /cart.js, then:
  //  - Removes orphan surcharges (parent was removed)
  //  - Adjusts surcharge qty to match parent qty
  // After any change, does a full cart refresh so totals + badge update correctly.
  // Uses _pendingSync to queue any DOM changes that arrive while sync is running.
  function syncSurcharges() {
    if (_isSyncing) { _pendingSync = true; return; }
    _isSyncing = true;
    _pendingSync = false;

    var _didWork = false;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        var surcharges = cart.items.filter(isScItem);
        if (!surcharges.length) return;

        var tasks = [];
        surcharges.forEach(function (sc) {
          var parent = findParent(cart.items, sc);
          if (!parent) {
            tasks.push(cartFetch('/cart/change.js', { id: sc.key, quantity: 0 }));
          } else if (parent.quantity !== sc.quantity) {
            tasks.push(cartFetch('/cart/change.js', { id: sc.key, quantity: parent.quantity }));
          }
        });

        if (!tasks.length) return;

        _didWork = true;
        return Promise.all(tasks).then(function () {
          return isCartPage() ? refreshMainCart() : refreshCartDrawer();
        });
      })
      .catch(function (err) {
        console.error('[YT Surcharge] Sync error:', err);
      })
      .finally(function () {
        if (_didWork) {
          // After re-render, wait for observer mutations to settle before releasing lock.
          // This prevents our own render from immediately triggering another sync loop.
          setTimeout(function () {
            _isSyncing = false;
            if (_pendingSync) { _pendingSync = false; syncSurcharges(); }
          }, 200);
        } else {
          // No work done — release lock immediately and run any queued sync
          _isSyncing = false;
          if (_pendingSync) { _pendingSync = false; syncSurcharges(); }
        }
      });
  }


  // ── Step 4: Observe cart DOM for changes → trigger sync ──────────────────
  //
  // cart-drawer (subtree): catches both full re-renders and inline qty changes.
  // #main-cart  (direct children): catches section re-renders on /cart page.
  function attachObserver() {
    function makeObserver(target, useSubtree) {
      new MutationObserver(function () {
        if (_isSyncing) {
          _pendingSync = true; // Remember: re-run sync after current one completes
          return;
        }
        clearTimeout(_syncTimer);
        _syncTimer = setTimeout(syncSurcharges, SYNC_DEBOUNCE_MS);
      }).observe(target, { childList: true, subtree: useSubtree });
    }

    var cartDrawer = getCartDrawer();
    if (cartDrawer) makeObserver(cartDrawer, true);

    var mainCart = document.getElementById('main-cart');
    if (mainCart) makeObserver(mainCart, false);
  }

  attachObserver();


  // ── Step 5: Block manual removal of surcharge items ──────────────────────
  //
  // Primary guard: Liquid hides cart-remove-button for surcharge items.
  // This JS guard is a safety net for edge cases (e.g. browser extensions).
  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('cart-remove-button');
    if (!removeBtn) return;
    if (removeBtn.closest('.yt-power-surcharge-item')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true /* capture */);


  // ── Step 6: Checkout gate — verify via API before allowing payment ────────
  //
  // If the customer changes parent qty then immediately clicks Checkout while a
  // sync is still running, they could pay for mismatched quantities. This gate:
  //  1. Skips entirely if no surcharges are present (zero added latency)
  //  2. Waits for any in-progress sync to complete
  //  3. Verifies cart state via /cart.js
  //  4. Fixes any mismatch before redirecting to /checkout
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="checkout"]');
    if (!btn) return;

    // Fast path: no surcharges visible in DOM and nothing syncing → proceed normally
    var hasSurcharges = !!document.querySelector('[data-surcharge-key]');
    if (!hasSurcharges && !_isSyncing) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var span     = btn.querySelector('span');
    var origText = span ? span.textContent : '';

    function doVerify() {
      if (span) span.textContent = 'Verifying cart…';
      btn.disabled = true;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var surcharges = cart.items.filter(isScItem);
          if (!surcharges.length) { window.location.href = '/checkout'; return; }

          var fixes = [];
          surcharges.forEach(function (sc) {
            var parent = findParent(cart.items, sc);
            if (!parent) {
              fixes.push(cartFetch('/cart/change.js', { id: sc.key, quantity: 0 }));
            } else if (parent.quantity !== sc.quantity) {
              fixes.push(cartFetch('/cart/change.js', { id: sc.key, quantity: parent.quantity }));
            }
          });

          if (fixes.length) {
            return Promise.all(fixes).then(function () { window.location.href = '/checkout'; });
          }
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          console.error('[YT Surcharge] Checkout gate failed:', err);
          // Fail open — never permanently block checkout
          if (span) span.textContent = origText;
          btn.disabled = false;
          window.location.href = '/checkout';
        });
    }

    if (_isSyncing) {
      // Wait for in-progress sync to finish, then verify
      var waited   = 0;
      var waitMax  = 4000; // Safety: give up waiting after 4s
      var waitTick = 100;
      var interval = setInterval(function () {
        waited += waitTick;
        if (!_isSyncing || waited >= waitMax) {
          clearInterval(interval);
          doVerify();
        }
      }, waitTick);
    } else {
      doVerify();
    }
  }, true /* capture — fires before Shopify's submit handler */);

})();
